import React, { useState, useRef, useEffect } from "react";
import { format } from "date-fns";
import DatePicker from "react-datepicker";
import { toast } from 'react-toastify';
import { startOfMonth, endOfMonth, isAfter } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

const DateRangeDropdown = ({ selectedRange, onRangeChange }) => {
  const [open, setOpen] = useState(false);
  const [tempRange, setTempRange] = useState(selectedRange?.range);
  const [startDate, setStartDate] = useState(new Date(new Date().setMonth(new Date().getMonth() - 11)));
  const [endDate, setEndDate] = useState(new Date());
  const [dateError, setDateError] = useState(false);
  const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const dropdownRef = useRef(null);
  const alertConfigurations = {
    position: "top-right",
    autoClose: 5000,
    hideProgressBar: false,
    closeOnClick: false,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
    theme: "light",
  }

  const options = [
    { label: "Last 7 days", value: "7_days" },
    { label: "Last 28 days", value: "28_days" },
    { label: "Last 3 months", value: "3_months" },
    { label: "Last 6 months", value: "6_months" },
    { label: "Last 12 months", value: "12_months" },
    { label: "Last 16 months", value: "16_months" },
    { label: "Custom", value: "custom" }
  ];


  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setOpen(false);
    }
  };

  useEffect(() => {
    if (open) {
      setTempRange(selectedRange?.range);
      if (selectedRange?.range === "custom") {
        setStartDate(new Date(formatInTimeZone(selectedRange.start_date, userTimeZone, 'yyyy/MM/dd')));
        setEndDate(new Date(formatInTimeZone(selectedRange.end_date, userTimeZone, 'yyyy/MM/dd')));
      }
    }
  }, [open]);

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApply = () => {
    if (tempRange === "custom" && isAfter(startDate, endDate)) {
      toast.error('Start date cannot be after end date.', alertConfigurations);
      setDateError(true);
      return;
    }
    setDateError(false);
    setOpen(false);
    if (tempRange === "custom") {
      const adjustedStartDate = startOfMonth(startDate);
      const adjustedEndDate = endOfMonth(endDate);
      onRangeChange({
        range: "custom",
        adjustedStartDate,
        adjustedEndDate
      });
    } else {
      onRangeChange({
        range: tempRange
      });
    }
  };

  return (
    <div className="position-relative d-inline-block" ref={dropdownRef}>
      <button
        className="months-filter"
        onClick={() => setOpen(!open)}
      >
        {selectedRange?.range === 'custom' && selectedRange.start_date && selectedRange.end_date
          ? `${formatInTimeZone(selectedRange.start_date, userTimeZone, 'yyyy/MM/dd')} - ${formatInTimeZone(selectedRange.end_date, userTimeZone, 'yyyy/MM/dd')}`
          : options.find(opt => opt.value === selectedRange?.range)?.label || "Select Range"}
        <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
          <path d="M3.25 4.875L6.5 8.125L9.75 4.875" stroke="#303030" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div className="card shadow p-4 mt-2 responsive-card" style={{
          position: "absolute",
          top: "100%",
          right: 0,
          zIndex: 1000,
          borderRadius: "16px"
        }}>
          <h5 className="mb-3">Date range</h5>
          <form>
            {options.map((opt) => (
              <div className="form-check mb-1" key={opt.value}>
                <input
                  className="form-check-input"
                  type="radio"
                  name="dateRange"
                  id={opt.value}
                  value={opt.value}
                  checked={tempRange === opt.value}
                  onChange={() => setTempRange(opt.value)}
                />
                <label className="form-check-label" htmlFor={opt.value}>
                  {opt.label}
                </label>
              </div>
            ))}

            {tempRange === "custom" && (
              <div className="mt-3">
                <label>Start date</label>
                <DatePicker
                  selected={startDate}
                  onChange={(date) => {
                    setStartDate(date);
                  }}
                  className={`form-control ${dateError ? 'border border-danger' : ''}`}
                  dateFormat="yyyy/MM/dd"
                  selectsStart
                  startDate={startDate}
                  endDate={endDate}
                />
                <label className="mt-2">End date</label>
                <DatePicker
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  className={`form-control ${dateError ? 'border border-danger' : ''}`}
                  dateFormat="yyyy/MM/dd"
                  selectsEnd
                  startDate={startDate}
                  endDate={endDate}
                  minDate={startDate}
                />
              </div>
            )}

            <div className="mt-3 d-flex justify-content-end">
              <button
                type="button"
                className="btn btn-link mr-2"
                onClick={() => setOpen(false)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={handleApply}
                disabled={tempRange === "custom" && (!startDate || !endDate)}
              >
                Apply
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default DateRangeDropdown;